import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import {
  fetchBusinessGroups,
  fetchSkillsByBusinessGroup,
  addSkillsByBusinessGroup,
  updateSkill,
  deleteSkill,
} from "PlatForms/OrgLMS/Services/AdminServices/skillsInventoryService";

const useSkillsInventory = () => {
  const [businessGroups, setBusinessGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [skills, setSkills] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadBusinessGroups = useCallback(async () => {
    try {
      setLoadingGroups(true);
      const data = await fetchBusinessGroups();
      const list = Array.isArray(data) ? data : [];
      setBusinessGroups(list);
      if (list.length > 0) {
        setSelectedGroupId((prev) => prev ?? list[0].id);
      }
    } catch (error) {
      console.error("Error loading business groups:", error);
      message.error("Failed to load business groups.");
      setBusinessGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  const loadSkills = useCallback(async (groupId) => {
    if (!groupId) {
      setSkills([]);
      return;
    }
    try {
      setLoadingSkills(true);
      const data = await fetchSkillsByBusinessGroup(groupId);
      setSkills(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading skills:", error);
      message.error("Failed to load skills for this business group.");
      setSkills([]);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  useEffect(() => {
    loadBusinessGroups();
  }, [loadBusinessGroups]);

  useEffect(() => {
    loadSkills(selectedGroupId);
  }, [selectedGroupId, loadSkills]);

  const addSkills = async (names) => {
    const clean = (names || []).map((n) => n.trim()).filter(Boolean);
    if (!selectedGroupId || clean.length === 0) return false;
    try {
      setSaving(true);
      await addSkillsByBusinessGroup(selectedGroupId, clean);
      message.success(clean.length > 1 ? "Skills added." : "Skill added.");
      await loadSkills(selectedGroupId);
      return true;
    } catch (error) {
      console.error("Error adding skills:", error);
      message.error("Failed to add skills.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const editSkill = async (skillId, name) => {
    const clean = (name || "").trim();
    if (!skillId || !clean) return false;
    try {
      setSaving(true);
      await updateSkill(skillId, clean);
      message.success("Skill updated.");
      await loadSkills(selectedGroupId);
      return true;
    } catch (error) {
      console.error("Error updating skill:", error);
      message.error("Failed to update skill.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const removeSkill = async (skillId) => {
    try {
      setSaving(true);
      await deleteSkill(skillId);
      message.success("Skill removed.");
      await loadSkills(selectedGroupId);
      return true;
    } catch (error) {
      console.error("Error removing skill:", error);
      message.error("Failed to remove skill.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    businessGroups,
    selectedGroupId,
    setSelectedGroupId,
    skills,
    loadingGroups,
    loadingSkills,
    saving,
    addSkills,
    editSkill,
    removeSkill,
  };
};

export default useSkillsInventory;
